import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { bindActionCreators } from "redux";
import { connect, type ConnectedProps } from "react-redux";
import { type AppDispatch, type RootState } from "../../Application/globaltypes/redux";
import { deleteAccountThunk, fetchAccounts } from "./state/thunks/allAccountsOverviewThunk";
import { bindAction } from "interfaces";
import { OverviewHeader, SearchInput } from "components";
import { useNavigate } from "react-router";
import moboConfigurator from "moboConfigurator";
import Restricted from "features/Application/Restricted";
import { Button } from "components/buttons/button/Button";
import { RolePermissions, RouteNames, type SortingDirection, ViewType } from "enums";
import DeleteAccountConfirmationModal from "./DeleteAccountModal/DeleteAccountModal";
import GenericItemsView from "views/ItemsView/GenericItemsView";
import { columnToParam, getColumnOptions } from "./getColumnOptions";
import { DeleteAccountOperationSuccess } from "features/Application/services/realTimeNotification/events/accounts/accountsEvents";
import AccountsNoResults from "./AccountsNoResults/AccountsNoResults";
import { type Account } from "interfaces/Account";
import { PermissionType } from "../types";
import { FilterForm } from "./Filters/FilterForm";
import { fetchFilters } from "./state/thunks/accountsFiltersThunk";
import type { FilterFormPropsBase } from "components/filterForms/types/props";
import { type FiltersEnum } from "features/Accounts/AllAccountsOverview/types";
import useAccountsQueryParams from "./hooks/useAccountsQueryParams";
import { useObservable } from "hooks/useObservable";

export type AccountOverviewProps = PropsFromRedux;
const createPermissions = [RolePermissions.AccountsSettingsCreate, RolePermissions.TemplatesManage];

export const AllAccountsOverview = (props: AccountOverviewProps) => {
  const {
    onFetchAccounts,
    accounts,
    accountsTotalCount,
    isLoading,
    deleteAccount,
    filterOptions,
    filterOptionsLoading,
    getFilterOptions,
    hasGlobalAccess,
  } = props;
  const [selectedAccountsAmount, setSelectedAccountsAmount] = useState(0);
  const reloadListItemsRef = useRef<(enableSorting: boolean) => void>();
  const navigate = useNavigate();
  const searchWasJustChangedRef = useRef(false);
  const preventInitialReloadRef = useRef(true);

  const deleteAccountObserver = useObservable<(onConfirm: () => void, id: number, name: string) => void>();

  const {
    filters,
    search,
    page,
    pageSize,
    sortBy,
    sortDirection,
    setSearch,
    setFilters,
    resetFilters,
    setSort,
    setPagination,
    sortingColumnName: currentSortingColumnName,
  } = useAccountsQueryParams();

  useEffect(() => {
    // Prevent the page from being reset on initial load if we are navigating back to this page
    if (preventInitialReloadRef.current) {
      preventInitialReloadRef.current = false;
      return;
    }
    reloadListItemsRef.current && reloadListItemsRef.current(true);
  }, [search]);

  const getAccounts = (
    skip: number,
    top: number,
    requestedSortingColumnName: string = "",
    orderDirection: SortingDirection | undefined = undefined,
  ) => {
    const trimmedSearch = search.trim();
    const normalizedColumnName = requestedSortingColumnName?.toLowerCase();
    const shouldIgnoreSort = searchWasJustChangedRef.current && trimmedSearch.length > 0;

    const resolvedOrderProp =
      !shouldIgnoreSort && normalizedColumnName
        ? columnToParam[normalizedColumnName as keyof typeof columnToParam]
        : undefined;
    const orderProp = shouldIgnoreSort ? undefined : resolvedOrderProp;
    const nextPageSize = top ?? pageSize;
    const nextPage = nextPageSize ? Math.floor((skip ?? 0) / nextPageSize) + 1 : page;

    if (nextPageSize && (nextPage !== page || nextPageSize !== pageSize)) {
      setPagination(nextPage, nextPageSize);
    }

    if (!shouldIgnoreSort && orderProp && orderDirection) {
      if (orderProp !== sortBy || orderDirection !== sortDirection) {
        setSort(orderProp, orderDirection);
      }
    }

    onFetchAccounts({
      skip,
      take: nextPageSize,
      orderProp,
      orderDirection,
      searchText: search,
      filters,
    });

    searchWasJustChangedRef.current = false;
  };

  const goToAccountCreation = () => {
    navigate("create");
  };

  const onDeleteAccount = useCallback(
    (id: number, name: string) => {
      deleteAccountObserver.notify(() => deleteAccount(id, name), id, name);
    },
    [deleteAccountObserver, deleteAccount],
  );

  const renderCreateAccountButton = () => {
    return (
      <Restricted
        permissions={createPermissions}
        renderContent={(hasAnyPermissions) => (
          <Button
            disabled={!hasAnyPermissions}
            floated="right"
            primary
            content="Create Account"
            onClick={goToAccountCreation}
            className="create-button accounts"
          />
        )}
      />
    );
  };

  const actionsDisabled = selectedAccountsAmount > 0;
  const options = useMemo(() => {
    return getColumnOptions({
      actionsDisabled,
      url: getUrl,
      onDeleteAccount,
      onOpenMobo: moboConfigurator.openMobo,
    });
  }, [actionsDisabled, onDeleteAccount]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      searchWasJustChangedRef.current = true;
    },
    [setSearch],
  );

  const handleApplyFilter = useCallback(
    (nextFilters: FilterFormPropsBase<FiltersEnum>["filter"]) => {
      setFilters(nextFilters);
    },
    [setFilters],
  );

  const handleResetFilters = useCallback(() => {
    resetFilters();
  }, [resetFilters]);

  const handleSelectedItemChange = (items: number[]) => {
    setSelectedAccountsAmount(items.length);
  };

  const getFilterForm = (filterFormProps: FilterFormPropsBase<FiltersEnum>) => (
    <FilterForm {...filterFormProps} showAccountType={hasGlobalAccess} />
  );

  return (
    <section className="nested-content accounts">
      <OverviewHeader title="Accounts" itemName="Account" selectedItemsAmount={selectedAccountsAmount}>
        {renderCreateAccountButton()}
      </OverviewHeader>

      <GenericItemsView
        className="alignment-padding"
        viewType={ViewType.LIST}
        columnOptions={options}
        fetchData={getAccounts}
        items={accounts}
        dataCount={accountsTotalCount}
        isLoading={isLoading}
        onSelectedItemChanged={handleSelectedItemChange}
        renderSearch={() => (
          <SearchInput placeholder="Search for Accounts..." onChange={handleSearchChange} defaultValue={search} />
        )}
        setReloadListItems={(cb) => (reloadListItemsRef.current = cb)}
        noResultsContent={
          <AccountsNoResults renderCreateAccountButton={renderCreateAccountButton} canCreate filtered={!!search} />
        }
        listViewRtnEvents={[DeleteAccountOperationSuccess]}
        getFilterForm={getFilterForm}
        appliedFilter={filters}
        applyFilter={handleApplyFilter}
        filterOptions={filterOptions}
        filterOptionsLoading={filterOptionsLoading}
        getFilterOptions={getFilterOptions}
        resetFilter={handleResetFilters}
        pagination={page}
        defaultSortingColumnName={currentSortingColumnName}
        sortingDirection={sortDirection}
      />
      <DeleteAccountConfirmationModal deleteAccountObserver={deleteAccountObserver} />
    </section>
  );
};

const getUrl = (account: Account) => {
  if (account.canView) {
    return `/${RouteNames.linkedAccountsManagement}/${account.id}`;
  }

  if (account.permissionType === PermissionType.Limited) {
    return `/${RouteNames.accounts}/limited/${account.id}`;
  }

  if (account.permissionType === PermissionType.View) {
    return `/${RouteNames.accounts}/view-only/${account.id}`;
  }

  return "";
};

const mapStateToProps = (state: RootState) => {
  const { userProfile } = state;
  const overview = state.accounts.overview;

  return {
    accounts: overview.grid.items,
    accountsTotalCount: overview.grid.itemsCount,
    isLoading: overview.grid.isLoading,
    filterOptionsLoading: overview.filters.isLoading,
    filterOptions: overview.filters.filterOptions,
    isMobo: userProfile.isMobo,
    hasGlobalAccess: userProfile.hasGlobalAccess,
  };
};

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  onFetchAccounts: bindActionCreators(fetchAccounts, dispatch),
  deleteAccount: bindAction(deleteAccountThunk, dispatch),
  getFilterOptions: bindAction(fetchFilters, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(AllAccountsOverview);
export default ConnectedComponent;
